/**
 * Feature Flags Storage Sync Plugin (client-only)
 *
 * Cross-tab synchronization for the feature flags store, mirroring
 * `auth.storage-sync.ts`. When one tab refetches the flags and persists the new
 * state via `saveFeatureFlagsState`, the other tabs receive the `storage` event
 * and reinject the state without re-calling the API (D9, FR-013).
 *
 * Note: The 'storage' event only fires when localStorage is changed by ANOTHER
 * window/tab, not the current one.
 *
 * See spec 030 — internal-contracts.md § 4.3.
 */

import { createFeatureFlagsStorageSyncHandler } from '~/utils/feature-flags.utils'
import { useFeatureFlagsStore } from '~/stores/featureFlags'

export default defineNuxtPlugin(nuxtApp => {
  // Only run on client side
  if (!import.meta.client) {
    return
  }

  // Use $pinia from nuxtApp to ensure Pinia context is available
  const pinia = nuxtApp.$pinia
  const featureFlagsStore = useFeatureFlagsStore(pinia)

  const storageHandler = createFeatureFlagsStorageSyncHandler({
    onFlagsChange: (state) => {
      featureFlagsStore.applyExternalState(state)
    },
  })

  // Kept active for the lifetime of the app; cleaned up by the browser on unload.
  window.addEventListener('storage', storageHandler)

  // Provide cleanup function for manual removal if needed (e.g., for testing)
  return {
    provide: {
      featureFlagsStorageSyncCleanup: () => {
        window.removeEventListener('storage', storageHandler)
      },
    },
  }
})
